<template>
  <page-template :is-loading="isLoading" :data-exists="aclProfiles !== null">
    <template #pageHeadline>
      {{ $t('aclProfile.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="aclProfiles"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="acl-profiles-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewAclProfilePath()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateAclProfile"
        @delete-row-button-clicked="openDeleteAclProfileModal"
        @download-data="downloadDoc"
      >
      </rbz-table>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="loadAclProfiles"
      />
      <rbz-modal-dialog
        :show="showDeleteAclProfileModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteAclProfile"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteAclProfileModal = false"
        @closeModal="showDeleteAclProfileModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedAclProfile.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { AclProfile, Action, DocumentName } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'AclProfilesList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    GitHistory,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('aclProfile.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getAclProfilePath(item);
          },
        },
        {
          id: 'action',
          title: this.$t('aclProfile.listPage.action'),
          fieldNames: ['action'],
          displayFunction: (item: AclProfile) => {
            const action = _.find(this.actions, (action) => {
              return action.id === item.action;
            });
            return action?.name || '';
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-200px action-cell',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getAclProfilePath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      aclProfiles: null as AclProfile[],
      pageLoadingCounter: null,

      showDeleteAclProfileModal: false,
      selectedAclProfile: null,

      actions: [] as Action[],
      referencedIds: [],
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('ACL Profiles List') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          await this.loadAclProfiles();
          await this.loadActions();
          await this.loadReferencedIds();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('aclProfile.plural'),
        singular: this.$t('aclProfile.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/acl-profiles/versions`;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadAclProfiles() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/acl-profiles`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.aclProfiles = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
      });
      const securityPolicies = response?.data?.items || [];
      const referencedIds: string[] = [];

      _.forEach(securityPolicies, (securityPolicy) => {
        _.forEach(securityPolicy.map, (location) => {
          referencedIds.push(location['acl_profile']);
        });
      });
      this.referencedIds = _.uniq(_.flatten(referencedIds));
      this.setPageLoadingStatus(false);
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/actions`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.actions = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(aclProfile: AclProfile) {
      return this.isAclProfileProtected(aclProfile) || this.isAclProfileReferenced(aclProfile);
    },

    isAclProfileProtected(aclProfile: AclProfile): boolean {
      return aclProfile.id.startsWith('__');
    },

    isAclProfileReferenced(aclProfile: AclProfile): boolean {
      return this.referencedIds.includes(aclProfile.id);
    },

    deleteTooltip(aclProfile: AclProfile): string {
      if (this.isAclProfileProtected(aclProfile)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.isAclProfileReferenced(aclProfile)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    getAclProfilePath(aclProfile: AclProfile) {
      return this.getEditPath('acl-profiles', aclProfile.id);
    },

    getNewAclProfilePath() {
      return this.getNewPath('acl-profiles');
    },

    editAclProfile(aclProfile: AclProfile) {
      this.navigateTo(this.getAclProfilePath(aclProfile));
    },

    async duplicateAclProfile(aclProfile: AclProfile) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/acl-profiles/${aclProfile.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalAclProfile = getOriginalResponse.data;
      const aclProfileToAdd = _.cloneDeep(originalAclProfile);
      aclProfileToAdd.id = DatasetsUtils.generateUUID2();
      aclProfileToAdd.name = Utils.generateUniqueEntityName(
        aclProfileToAdd.name,
        _.map(this.aclProfiles, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/acl-profiles/${aclProfileToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        methodName: 'POST',
        url,
        data: aclProfileToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editAclProfile(aclProfileToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteAclProfile() {
      this.setPageLoadingStatus(true);
      const aclProfileId = this.selectedAclProfile.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/acl-profiles/${aclProfileId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.aclProfiles = _.filter(this.aclProfiles, (aclProfile) => {
          return aclProfile.id !== aclProfileId;
        });
      }
      this.showDeleteAclProfileModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('acl-profiles', 'json', this.aclProfiles);
    },

    openDeleteAclProfileModal(aclProfile: AclProfile) {
      this.showDeleteAclProfileModal = true;
      this.selectedAclProfile = aclProfile;
    },
  },
});
</script>
